import React, { Suspense } from 'react';

// Drop-in replacement for the (unmaintained, non-React-19) `react-loadable`,
// implemented with React.lazy + Suspense. Supports the subset the app uses:
//   Loadable({ loader, loading, render? })     - single module
//   Loadable.Map({ loader: {...}, loading, render })  - multiple modules
//
// `loader` returns a dynamic import; `render(loaded, props)` (when present) lets
// callers use the module's named exports. Without `render`, the module's default
// export is used. `loading` is rendered as the Suspense fallback.

interface LoadableOpts {
  loader: () => Promise<any>;
  loading?: React.ComponentType<any>;
  render?: (loaded: any, props: any) => React.ReactNode;
}

interface LoadableMapOpts {
  loader: { [key: string]: () => Promise<any> };
  loading?: React.ComponentType<any>;
  render: (loaded: { [key: string]: any }, props: any) => React.ReactNode;
}

function withSuspense(Lazy: React.LazyExoticComponent<any>, Loading?: React.ComponentType<any>) {
  function LoadableComponent(props: any) {
    const fallback = Loading ? <Loading /> : null;

    return (
      <Suspense fallback={fallback}>
        <Lazy {...props} />
      </Suspense>
    );
  }

  return LoadableComponent;
}

export default function Loadable(opts: LoadableOpts) {
  const { loader, loading, render } = opts;

  const Lazy = React.lazy(async () => {
    const loaded = await loader();

    if (render) {
      return { default: (props: any) => render(loaded, props) };
    }

    return { default: loaded.default || loaded };
  });

  return withSuspense(Lazy, loading);
}

Loadable.Map = function LoadableMap(opts: LoadableMapOpts) {
  const { loader, loading, render } = opts;

  const Lazy = React.lazy(async () => {
    const keys = Object.keys(loader);
    const modules = await Promise.all(keys.map(key => loader[key]()));

    const loaded: { [key: string]: any } = {};
    keys.forEach((key, index) => { loaded[key] = modules[index]; });

    return { default: (props: any) => render(loaded, props) };
  });

  return withSuspense(Lazy, loading);
};
